import posthog from 'posthog-js'
import { enrollmentStore } from '@/stores'

const posthogKey = import.meta.env.VITE_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'
const requiredLocalStorageKeys = ['_gcl_ls']
let posthogInitialized = false
let posthogWatchStarted = false
let posthogIdentitySignature: string | null = null

type PostHogIdentity = {
  distinctId: string
  personProperties: Record<string, string>
}

function normalizeIdentityValue(value?: string | null) {
  const normalized = value?.trim()
  return normalized ? normalized : null
}

function getNormalizedLowercaseValue(value?: string | null) {
  return normalizeIdentityValue(value)?.toLowerCase() ?? null
}

function getPostHogEmail(authSessionEmail?: string | null) {
  if (authSessionEmail) {
    return getNormalizedLowercaseValue(authSessionEmail)
  }

  const enrollmentState = enrollmentStore.state
  return (
    getNormalizedLowercaseValue(enrollmentState.userProfile?.email) ||
    getNormalizedLowercaseValue(enrollmentState.formData.email)
  )
}

function getPostHogIdentity(): PostHogIdentity | null {
  const enrollmentState = enrollmentStore.state
  const email = getPostHogEmail()
  const userId = normalizeIdentityValue(
    enrollmentState.userProfile?.id?.toString() ??
      enrollmentState.userProfile?.enrollmentId?.toString() ??
      null
  )
  const fallbackFullName = [enrollmentState.userProfile?.firstName, enrollmentState.userProfile?.lastName]
    .filter(Boolean)
    .join(' ')
  const preferredFullName = enrollmentState.userProfile?.fullName || fallbackFullName || null
  const fullName = normalizeIdentityValue(preferredFullName)

  if (!email && !userId) {
    return null
  }

  const distinctId = email || `user:${userId}`
  const personProperties: Record<string, string> = {}

  if (email) {
    personProperties.email = email
  }

  if (fullName) {
    personProperties.name = fullName
  }

  if (userId) {
    personProperties.userId = userId
  }

  return {
    distinctId,
    personProperties
  }
}

function syncPostHogIdentity() {
  if (!posthogInitialized) {
    return
  }

  const identity = getPostHogIdentity()

  if (!identity) {
    if (posthogIdentitySignature) {
      posthog.reset()
      posthogIdentitySignature = null
    }
    return
  }

  const nextSignature = JSON.stringify(identity)

  if (nextSignature === posthogIdentitySignature) {
    return
  }

  posthog.identify(identity.distinctId, identity.personProperties)
  posthogIdentitySignature = nextSignature
}

function hasStorageKeys(storage: Storage, keys: string[]) {
  return keys.every(key => {
    const value = storage.getItem(key)
    return typeof value === 'string' && value.length > 0
  })
}

function hasRequiredPostHogStorage() {
  if (typeof window === 'undefined') {
    return false
  }

  return hasStorageKeys(window.localStorage, requiredLocalStorageKeys)
}

function tryInitializePostHog() {
  if (
    posthogInitialized ||
    !posthogKey ||
    !hasRequiredPostHogStorage()
  ) {
    return
  }

  posthog.init(posthogKey, {
    api_host: posthogHost,
    defaults: '2026-01-30',
    capture_pageview: 'history_change',
    capture_pageleave: true
  })

  posthogInitialized = true
  syncPostHogIdentity()
}

function startPostHogStorageWatch() {
  if (posthogWatchStarted || typeof window === 'undefined') {
    return
  }

  posthogWatchStarted = true

  const checkStorageAndInitialize = () => {
    tryInitializePostHog()
    syncPostHogIdentity()
  }

  window.addEventListener('storage', checkStorageAndInitialize)
  window.addEventListener('focus', checkStorageAndInitialize)
  document.addEventListener('visibilitychange', checkStorageAndInitialize)
  enrollmentStore.subscribe(checkStorageAndInitialize)
}

export function initializePostHog() {
  startPostHogStorageWatch()
  tryInitializePostHog()
}
